(function () {
  'use strict';

  const STATE = {
    customiserModal: null,
    customiserContent: null,
    customiserLoading: null,
    cartActionsSyncTimer: null,
    cartActionsSyncInFlight: false,
    cartActionsObserver: null,
    cartDrawerOpenObserver: null,
    removingCustomisationKeys: new Set(),
    latestCartPayload: null,
    pendingCartPayload: null,
    lastCartUpdatedAt: 0,
  };

  const CUSTOMISER_MODAL_ID = 'NewLogoCustomiserModal';
  const CART_ACTIONS_SYNCED_EVENT = 'new-logo:cart-actions-synced';
  const LEGACY_LOGO_PROPERTY_KEYS = new Set([
    'Logo Added',
    'Logo Positions',
    'Logo Method',
    'Logo Size',
    'Logo Type',
    'Logo Setup Required',
    'Logo Artwork',
    'Logo Artwork Filename',
    'Logo Notes',
    'Logo Group',
    'Logo Placement',
  ]);

  function config() {
    return window.NewLogoConfig || {};
  }

  function emitCartActionsSynced(reason) {
    document.dispatchEvent(
      new CustomEvent(CART_ACTIONS_SYNCED_EVENT, {
        detail: {
          reason: String(reason || ''),
          at: Date.now(),
        },
      })
    );
  }

  function normalizeGroup(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getCartPayload(candidate) {
    if (!candidate || typeof candidate !== 'object') return null;
    if (!Array.isArray(candidate.items)) return null;
    return candidate;
  }


  function ensureCustomiserModal() {
    if (STATE.customiserModal) return STATE.customiserModal;

    const modal = document.createElement('basic-modal');
    modal.className = 'drawer modal cart-logo-modal';
    modal.id = CUSTOMISER_MODAL_ID;
    modal.setAttribute('hidden', '');
    modal.innerHTML = `
      <div class="fixed-overlay" aria-controls="${CUSTOMISER_MODAL_ID}"></div>
      <div class="drawer__inner">
        <button
          class="drawer__close-btn z-2"
          aria-controls="${CUSTOMISER_MODAL_ID}"
          aria-label="Close"
          type="button"
        >
          ×
        </button>
        <div class="drawer__body cart-logo-modal__body">
          <div class="cart-logo-modal__loading" data-logo-modal-loading>Loading customiser...</div>
          <div class="cart-logo-modal__content" data-logo-modal-content></div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    STATE.customiserModal = modal;
    STATE.customiserContent = modal.querySelector('[data-logo-modal-content]');
    STATE.customiserLoading = modal.querySelector('[data-logo-modal-loading]');

    modal.addEventListener('modal:handleAfterHide', () => {
      if (STATE.customiserContent) {
        STATE.customiserContent.innerHTML = '';
      }
      if (STATE.customiserLoading) {
        STATE.customiserLoading.hidden = false;
      }
    });

    return modal;
  }

  function closeCustomiserModal() {
    if (!STATE.customiserModal) return;
    if (typeof STATE.customiserModal.hide === 'function') {
      STATE.customiserModal.hide();
      return;
    }
    STATE.customiserModal.removeAttribute('open');
    STATE.customiserModal.hidden = true;
  }

  function closeCartDrawerIfOpen() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || !cartDrawer.hasAttribute('open')) return;

    if (typeof cartDrawer.hide === 'function') {
      cartDrawer.hide();
      return;
    }

    cartDrawer.removeAttribute('open');
  }

  function openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    if (typeof cartDrawer.show === 'function') {
      cartDrawer.show();
      return;
    }

    cartDrawer.hidden = false;
    cartDrawer.setAttribute('open', '');
  }

  function isCartPageContext() {
    if (document.body.classList.contains('cart-template')) return true;
    if (document.querySelector('main-cart-items')) return true;
    return false;
  }

  function isCartDrawerOpen() {
    const cartDrawer = document.querySelector('cart-drawer');
    return Boolean(cartDrawer && cartDrawer.hasAttribute('open'));
  }

  function shouldSyncCartLineActions() {
    return isCartPageContext() || isCartDrawerOpen();
  }

  function dispatchCartUpdatedFromPayload(cartPayload, source) {
    const safePayload = getCartPayload(cartPayload);
    if (!safePayload) return;

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart: safePayload,
          source: String(source || 'new-logo-explicit'),
        },
      })
    );
  }

  function dispatchExplicitCartRefresh(open, reason) {
    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        detail: {
          open: Boolean(open),
          force: true,
          reason: String(reason || ''),
          source: 'new-logo-explicit',
        },
      })
    );
  }

  function hasSectionsPayload(candidate) {
    const safeCart = getCartPayload(candidate);
    return Boolean(safeCart && safeCart.sections && typeof safeCart.sections === 'object');
  }

  async function openCustomiserModalFromUrl(url) {
    const modal = ensureCustomiserModal();
    if (!STATE.customiserContent || !STATE.customiserLoading) {
      window.location.href = url;
      return;
    }

    STATE.customiserLoading.hidden = false;
    STATE.customiserContent.innerHTML = '';
    if (typeof modal.show === 'function') {
      modal.show();
    } else {
      modal.hidden = false;
      modal.setAttribute('open', '');
    }

    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html',
        },
      });

      if (!response.ok) {
        throw new Error('Unable to load logo customiser.');
      }

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const customiserMarkup = parsed.querySelector('.logo-customiser');

      if (!customiserMarkup) {
        window.location.href = url;
        return;
      }

      const parsedUrl = new URL(url, window.location.origin);
      const customiserRootEl = customiserMarkup.querySelector('logo-customiser');
      if (customiserRootEl) {
        const line = parsedUrl.searchParams.get('line');
        const key = parsedUrl.searchParams.get('key');
        const group = parsedUrl.searchParams.get('customisation_group');
        const targetKeys = parsedUrl.searchParams.get('target_keys');

        if (line) customiserRootEl.setAttribute('data-target-line', line);
        if (key) customiserRootEl.setAttribute('data-target-key', key);
        if (group) customiserRootEl.setAttribute('data-customisation-group', group);
        if (targetKeys) customiserRootEl.setAttribute('data-target-keys', targetKeys);
        customiserRootEl.setAttribute('data-modal-mode', 'true');
        customiserRootEl.setAttribute('data-modal-id', CUSTOMISER_MODAL_ID);
      }

      STATE.customiserContent.innerHTML = customiserMarkup.outerHTML;

      STATE.customiserLoading.hidden = true;
    } catch (error) {
      console.error(error);
      window.location.href = url;
    }
  }

  function resolveGroupForCartItem(item) {
    const properties = item?.properties || {};
    const fromProps = normalizeGroup(properties._logo_customisation_group || properties['Logo Group'] || '');
    if (fromProps) return fromProps;

    const handle = String(item?.handle || '').trim();
    const mapped = normalizeGroup(config().products?.byHandle?.[handle] || '');
    if (mapped) return mapped;

    return '';
  }

  function getRuntimeOptionsForCartItem(item) {
    const runtime = window.NewLogoRuntime || {};
    const byHandle = runtime.customisationOptionsByHandle || {};
    const byProductId = runtime.customisationOptionsByProductId || {};

    const handle = String(item?.handle || '').trim();
    if (handle) {
      if (Object.prototype.hasOwnProperty.call(byHandle, handle)) {
        return byHandle[handle];
      }
      const lowerHandle = handle.toLowerCase();
      const matchedHandleKey = Object.keys(byHandle).find(
        (key) => String(key || '').trim().toLowerCase() === lowerHandle
      );
      if (matchedHandleKey) return byHandle[matchedHandleKey];
    }

    const productId = String(item?.product_id || '').trim();
    if (productId && Object.prototype.hasOwnProperty.call(byProductId, productId)) {
      return byProductId[productId];
    }

    return [];
  }

  function hasRuntimeCustomisationForCartItem(item) {
    const options = getRuntimeOptionsForCartItem(item);
    if (Array.isArray(options)) return options.length > 0;
    if (!options || typeof options !== 'object') return false;
    if (Array.isArray(options.options)) return options.options.length > 0;
    return Boolean(options.position_label || options.positionLabel || options.options);
  }

  function getLogoMethod(properties) {
    const method = String(properties?._logo_method || properties?.['Logo Method'] || '')
      .trim()
      .toLowerCase();
    if (method === 'print' || method === 'embroidery') return method;
    return '';
  }

  function getLogoType(properties) {
    const type = String(properties?._logo_type || properties?.['Logo Type'] || '')
      .trim()
      .toLowerCase();
    if (type === 'logo') return type;
    return '';
  }

  function getMethodIconMarkup(method) {
    if (method === 'print') {
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" focusable="false"><path d="M2.5 8a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1"/><path d="M5 1a2 2 0 0 0-2 2v2H2a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1v1a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1V3a2 2 0 0 0-2-2zM4 3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2H4zm1 5a2 2 0 0 0-2 2v1H2a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v-1a2 2 0 0 0-2-2zm7 2v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1"/></svg>`;
    }
    if (method === 'embroidery') {
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true" focusable="false"><path stroke-linejoin="round" d="M1.48291 6.99988c0.00643-1.39437 0.15366-2.81505 0.41703-4.56238h6.97872c0.26337 1.74733 0.4106 3.16827 0.41703 4.56264m-7.81251-0.01468c0.00642 1.39437 0.15366 2.81504 0.41703 4.56234l6.97818 0.0003c0.26337-1.74734 0.4106-3.16801 0.41703-4.56238"/><path stroke-linecap="round" stroke-linejoin="round" d="M5.38965 2.4375V0.625"/><path stroke-linecap="round" stroke-linejoin="round" d="m1.08691 2.4375 8.60656 0"/><path stroke-linecap="round" stroke-linejoin="round" d="m1.67284 4.62662 7.586 0.99368"/><path stroke-linecap="round" stroke-linejoin="round" d="M1.50703 7.80939 12.8991 9.32281"/><path stroke-linecap="round" stroke-linejoin="round" d="M1.08691 11.5625h8.60656"/><path stroke-linecap="round" stroke-linejoin="round" d="M5.38965 13.375v-1.8125"/></svg>`;
    }
    return '';
  }

  function getCartActionIconMarkup(action) {
    if (action === 'edit') {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pencil-square" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/><path fill-rule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"/></svg>`;
    }

    return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-plus-circle" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/><path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4"/></svg>`;
  }

  function getRemoveCustomisationIconMarkup() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-trash" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z"/><path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z"/></svg>`;
  }

  function titleCaseFromKey(value) {
    return String(value || '')
      .trim()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  function buildCustomisationTooltip(properties) {
    const source = properties || {};
    const positions = String(source._logo_positions || source['Logo Positions'] || '').trim();
    const method = String(source._logo_method || source['Logo Method'] || '').trim();
    const size = String(source._logo_size_tier || source['Logo Size'] || '').trim();

    const parts = [];
    if (positions) parts.push(`Positions: ${positions.replace(/\|/g, ', ')}`);
    if (method) parts.push(`Method: ${titleCaseFromKey(method)}`);
    if (size) parts.push(`Size: ${titleCaseFromKey(size)}`);

    return parts.join('\n');
  }

  async function fetchCartJson() {
    const response = await fetch(`/cart.js?_=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Unable to load cart data.');
    }

    return response.json();
  }

  async function getFreshCartPayload(changeResponseCart) {
    const fallbackPayload = getCartPayload(changeResponseCart);
    try {
      const freshCart = await fetchCartJson();
      if (fallbackPayload?.sections && typeof fallbackPayload.sections === 'object') {
        freshCart.sections = fallbackPayload.sections;
      }
      return freshCart;
    } catch (error) {
      return fallbackPayload || null;
    }
  }

  function collectGroupedSections() {
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );
    return sectionsToBundle;
  }

  function publishCartSectionsUpdate(cartPayload) {
    if (!cartPayload || typeof cartPayload !== 'object') return false;
    if (!cartPayload.sections || typeof cartPayload.sections !== 'object') return false;
    if (
      !window.FoxTheme ||
      !window.FoxTheme.pubsub ||
      !window.FoxTheme.pubsub.publish ||
      !window.FoxTheme.pubsub.PUB_SUB_EVENTS ||
      !window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate
    ) {
      return false;
    }

    window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      cart: cartPayload,
    });
    return true;
  }

  function stripLogoProperties(properties) {
    const source = properties || {};
    const nextProperties = {};

    Object.entries(source).forEach(([key, value]) => {
      if (value === '' || value === null || typeof value === 'undefined') return;
      if (String(key).startsWith('_logo_')) return;
      if (LEGACY_LOGO_PROPERTY_KEYS.has(String(key))) return;
      nextProperties[key] = value;
    });

    return nextProperties;
  }

  function clearLogoProperties(properties) {
    const source = properties || {};
    const nextProperties = {};

    Object.entries(source).forEach(([key, value]) => {
      const normalizedKey = String(key);
      const isLogoKey = normalizedKey.startsWith('_logo_') || LEGACY_LOGO_PROPERTY_KEYS.has(normalizedKey);
      if (isLogoKey) {
        nextProperties[normalizedKey] = '';
        return;
      }

      if (value === '' || value === null || typeof value === 'undefined') return;
      nextProperties[normalizedKey] = value;
    });

    return nextProperties;
  }

  function hasAnyLogoProperties(properties) {
    const source = properties || {};
    return Object.entries(source).some(([key, value]) => {
      const normalizedKey = String(key);
      if (!(normalizedKey.startsWith('_logo_') || LEGACY_LOGO_PROPERTY_KEYS.has(normalizedKey))) return false;
      if (value === '' || value === null || typeof value === 'undefined') return false;
      return true;
    });
  }

  async function changeLineProperties(id, quantity, properties) {
    const payload = {
      id,
      quantity,
      properties,
    };
    const sectionsToBundle = collectGroupedSections();
    if (sectionsToBundle.length) {
      payload.sections = sectionsToBundle;
      payload.sections_url = window.location.pathname;
    }

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error('Unable to update this line item.');
    }

    return response.json();
  }

  function resolveItemForCustomisationRemoval(items, context) {
    const sourceItems = Array.isArray(items) ? items : [];
    const trimmedKey = String(context?.key || '').trim();
    const trimmedToken = String(context?.token || '').trim();
    const variantId = String(context?.variantId || '').trim();
    const allowTokenFallback = Boolean(context?.allowTokenFallback);
    const line = Number.isFinite(context?.line) ? context.line : Number.parseInt(String(context?.line || ''), 10);
    let keyFallback = null;
    let lineFallback = null;

    if (trimmedKey) {
      const byKey = sourceItems.find((item) => String(item?.key || '').trim() === trimmedKey);
      if (byKey && hasAnyLogoProperties(byKey?.properties || {})) return byKey;
      if (byKey) keyFallback = byKey;
    }

    if (Number.isFinite(line) && line > 0) {
      const byLine = sourceItems[line - 1] || null;
      if (byLine && hasAnyLogoProperties(byLine?.properties || {})) return byLine;
      if (byLine) lineFallback = byLine;
    }

    if (allowTokenFallback && trimmedToken) {
      const byToken = sourceItems.filter((item) => {
        const token = String(item?.properties?._logo_customiser_token || '').trim();
        if (!token || token !== trimmedToken) return false;
        if (!hasAnyLogoProperties(item?.properties || {})) return false;
        if (variantId && String(item?.variant_id ?? item?.id ?? '').trim() !== variantId) return false;
        return true;
      });
      if (byToken.length === 1) return byToken[0];
      if (byToken.length > 1) return byToken[0];
    }

    if (allowTokenFallback && variantId) {
      const byVariantWithLogo = sourceItems.filter((item) => {
        return String(item?.variant_id ?? item?.id ?? '').trim() === variantId && hasAnyLogoProperties(item?.properties || {});
      });
      if (byVariantWithLogo.length === 1) return byVariantWithLogo[0];
      if (byVariantWithLogo.length > 1) return byVariantWithLogo[0];

      const byVariantAny = sourceItems.filter((item) => {
        return String(item?.variant_id ?? item?.id ?? '').trim() === variantId;
      });
      if (byVariantAny.length === 1) return byVariantAny[0];
      if (byVariantAny.length > 1) return byVariantAny[0];
    }

    if (keyFallback) return keyFallback;
    if (lineFallback) return lineFallback;
    return null;
  }

  async function removeLineCustomisation(context) {
    const cart = await fetchCartJson();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    if (!items.length) return;

    let item = resolveItemForCustomisationRemoval(items, context);
    if (!item) {
      const fallbackLine = Number.isFinite(context?.line) ? context.line : Number.parseInt(String(context?.line || ''), 10);
      if (Number.isFinite(fallbackLine) && fallbackLine > 0 && items[fallbackLine - 1]) {
        item = items[fallbackLine - 1];
      }
    }
    if (!item) return cart;

    const strippedProperties = stripLogoProperties(item.properties || {});
    const clearedProperties = clearLogoProperties(item.properties || {});
    const resolvedLine = items.findIndex((cartItem) => String(cartItem?.key || '') === String(item.key || '')) + 1;

    // Prefer key-based updates first; keys are stable for the exact line item we rendered.
    let changedCart = null;
    let keyUpdateFailed = false;
    try {
      changedCart = await changeLineProperties(item.key, item.quantity, strippedProperties);
    } catch (error) {
      keyUpdateFailed = true;
    }

    if (keyUpdateFailed && resolvedLine > 0) {
      const keyFailPayload = {
        line: resolvedLine,
        quantity: item.quantity,
        properties: strippedProperties,
      };
      const sectionsToBundle = collectGroupedSections();
      if (sectionsToBundle.length) {
        keyFailPayload.sections = sectionsToBundle;
        keyFailPayload.sections_url = window.location.pathname;
      }
      const keyFailResponse = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(keyFailPayload),
      });
      if (!keyFailResponse.ok) {
        throw new Error('Unable to remove customisation from this line item.');
      }
      changedCart = await keyFailResponse.json();
    } else if (keyUpdateFailed) {
      throw new Error('Unable to remove customisation from this line item.');
    }

    let changedItems = Array.isArray(changedCart?.items) ? changedCart.items : [];
    const changedByOldKey = changedItems.find((cartItem) => String(cartItem?.key || '') === String(item?.key || '')) || null;
    const changedByLine = resolvedLine > 0 ? changedItems[resolvedLine - 1] || null : null;
    const changedTarget = changedByOldKey || changedByLine;
    let stillHasLogo = hasAnyLogoProperties(changedTarget?.properties || {});

    if (!stillHasLogo) {
      return changedCart;
    }

    // Fallback: explicit line update and clear logo keys to blank values.
    if (resolvedLine > 0) {
      const payload = {
        line: resolvedLine,
        quantity: item.quantity,
        properties: clearedProperties,
      };
      const sectionsToBundle = collectGroupedSections();
      if (sectionsToBundle.length) {
        payload.sections = sectionsToBundle;
        payload.sections_url = window.location.pathname;
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Unable to remove customisation from this line item.');
      }

      changedCart = await response.json();
      changedItems = Array.isArray(changedCart?.items) ? changedCart.items : [];
      const fallbackByOldKey =
        changedItems.find((cartItem) => String(cartItem?.key || '') === String(item?.key || '')) || null;
      const fallbackByLine = resolvedLine > 0 ? changedItems[resolvedLine - 1] || null : null;
      const fallbackTarget = fallbackByOldKey || fallbackByLine;
      stillHasLogo = hasAnyLogoProperties(fallbackTarget?.properties || {});
      if (!stillHasLogo) return changedCart;
    }

    // Final fallback: clear by exact line when available, otherwise by key.
    if (resolvedLine > 0) {
      const payload = {
        line: resolvedLine,
        quantity: item.quantity,
        properties: clearedProperties,
      };
      const sectionsToBundle = collectGroupedSections();
      if (sectionsToBundle.length) {
        payload.sections = sectionsToBundle;
        payload.sections_url = window.location.pathname;
      }
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error('Unable to remove customisation from this line item.');
      }
      return response.json();
    }

    return changeLineProperties(item.key, item.quantity, clearedProperties);
  }

  function buildCandidateCustomiserUrls(group, preferredUrl) {
    const pageConfig = config().pages || {};
    const mappedUrl = group ? pageConfig.map?.[group] : '';
    const prefix = String(pageConfig.prefix || 'add-logo-');
    const fallback = String(pageConfig.fallback || '/pages/add-logo');

    const candidates = [];
    if (preferredUrl) candidates.push(String(preferredUrl));
    if (mappedUrl) candidates.push(String(mappedUrl));
    if (group) {
      candidates.push(`/pages/${prefix}${group}`);
      candidates.push(`/pages/${group}`);
    }
    candidates.push(fallback);

    return Array.from(new Set(candidates.filter(Boolean)));
  }
  function resolveCustomiserUrlSync(group, preferredUrl) {
    const candidates = buildCandidateCustomiserUrls(group, preferredUrl);
    return candidates[0] || '/pages/add-logo';
  }

  function parseLineFromElement(itemEl) {
    const id = String(itemEl?.id || '');
    const match = id.match(/-(\d+)$/);
    if (match) {
      const line = Number.parseInt(match[1], 10);
      if (Number.isFinite(line) && line > 0) return line;
    }

    const indexInput = itemEl?.querySelector('[data-index]');
    const line = Number.parseInt(indexInput?.getAttribute('data-index') || '', 10);
    if (Number.isFinite(line) && line > 0) return line;

    return null;
  }

  function buildCartItemMapByElement(itemElements, cartItems) {
    const elements = Array.isArray(itemElements) ? itemElements : [];
    const items = Array.isArray(cartItems) ? cartItems : [];
    const byKey = new Map();
    const usedItemIndexes = new Set();
    const elementToItem = new Map();

    items.forEach((item, index) => {
      const key = String(item?.key || '').trim();
      if (!key || byKey.has(key)) return;
      byKey.set(key, { item, index });
    });

    // Prefer explicit key mapping if available.
    elements.forEach((itemEl) => {
      const key = String(itemEl?.getAttribute('data-cart-item-key') || '').trim();
      if (!key) return;
      const matched = byKey.get(key);
      if (!matched) return;
      if (usedItemIndexes.has(matched.index)) return;
      usedItemIndexes.add(matched.index);
      elementToItem.set(itemEl, matched);
    });

    const remainingElements = elements.filter((itemEl) => !elementToItem.has(itemEl));
    const remainingItems = items
      .map((item, index) => ({ item, index }))
      .filter((entry) => !usedItemIndexes.has(entry.index));

    const regularElements = [];
    const feeElements = [];
    remainingElements.forEach((itemEl) => {
      const variantId = String(itemEl?.getAttribute('data-variant-id') || '').trim();
      if (variantId === '55420858270081') {
        feeElements.push(itemEl);
      } else {
        regularElements.push(itemEl);
      }
    });

    const regularItems = [];
    const feeItems = [];
    remainingItems.forEach((entry) => {
      const variantId = String(entry?.item?.variant_id ?? entry?.item?.id ?? '').trim();
      if (variantId === '55420858270081') {
        feeItems.push(entry);
      } else {
        regularItems.push(entry);
      }
    });

    regularElements.forEach((itemEl, index) => {
      const matched = regularItems[index] || null;
      if (!matched) return;
      elementToItem.set(itemEl, matched);
      usedItemIndexes.add(matched.index);
    });

    feeElements.forEach((itemEl, index) => {
      const matched = feeItems[index] || null;
      if (!matched) return;
      elementToItem.set(itemEl, matched);
      usedItemIndexes.add(matched.index);
    });

    return elementToItem;
  }

  function isGiftWrapItemElement(itemEl) {
    return Boolean(itemEl?.querySelector('.cart-quantity-gift-wrap'));
  }

  function ensureActionHost(itemEl) {
    const details = itemEl.querySelector('.cart-item__details') || itemEl.querySelector('.cart-item__product--info') || itemEl;
    if (!details) return null;

    let host = details.querySelector('.new-logo-action-host');
    if (!host) {
      host = document.createElement('div');
      host.className = 'new-logo-action-host';
    }

    const actionRow = details.querySelector('.cart-item__action');
    if (actionRow) {
      if (host.parentElement !== details || host.nextElementSibling !== actionRow) {
        details.insertBefore(host, actionRow);
      }
    } else if (host.parentElement !== details) {
      details.appendChild(host);
    }

    return host;
  }

  function syncNativeLineBindings(itemEl, cartItem, line) {
    const safeLine = Number.parseInt(String(line || ''), 10);
    if (!Number.isFinite(safeLine) || safeLine <= 0) return;

    if (itemEl && String(itemEl.id || '').startsWith('CartDrawer-Item-')) {
      itemEl.id = `CartDrawer-Item-${safeLine}`;
    }

    const removeControl = itemEl?.querySelector('[is="cart-remove-item"], .cart-item__remove.btn-remove');
    if (removeControl) {
      removeControl.setAttribute('data-index', String(safeLine));
      const removeUrl = String(cartItem?.url_to_remove || '').trim();
      if (removeUrl) {
        removeControl.setAttribute('href', removeUrl);
      } else {
        removeControl.setAttribute('href', `/cart/change?line=${safeLine}&quantity=0`);
      }
    }

    const quantityInputs = itemEl?.querySelectorAll('.quantity__input[data-index], input[name="updates[]"][data-index]');
    quantityInputs?.forEach((input) => {
      input.setAttribute('data-index', String(safeLine));
    });
  }

  function upsertCartLineAction(itemEl, cartItem, line) {
    const properties = cartItem?.properties || {};
    if (properties._logo_charge_type != null || properties._logo_parent_key != null) {
      itemEl.querySelector('.new-logo-cart-action')?.remove();
      return;
    }

    if (isGiftWrapItemElement(itemEl)) {
      itemEl.querySelector('.new-logo-cart-action')?.remove();
      return;
    }

    const group = resolveGroupForCartItem(cartItem);
    const allowUngrouped = config().featureFlags?.allowUngroupedCartActions !== false;
    const hasRuntimeOptions = hasRuntimeCustomisationForCartItem(cartItem);
    const logoAddedValue = String(properties._logo_added || properties['Logo Added'] || '').toLowerCase();
    const hasCustomisationApplied =
      logoAddedValue === 'yes' || logoAddedValue === 'true' || logoAddedValue === '1' || getLogoMethod(properties) !== '';
    const isApplicable = hasCustomisationApplied || Boolean(group) || hasRuntimeOptions;

    if (!isApplicable) {
      itemEl.querySelector('.new-logo-cart-action')?.remove();
      return;
    }

    if (!group && !allowUngrouped && !hasCustomisationApplied) {
      itemEl.querySelector('.new-logo-cart-action')?.remove();
      return;
    }

    itemEl.querySelectorAll('.cart-item__options .new-logo-cart-action').forEach((staleAction) => staleAction.remove());

    const actionHost = ensureActionHost(itemEl);
    if (!actionHost) return;

    const actionMode = hasCustomisationApplied ? 'edit' : 'add';
    const triggerLabel = actionMode === 'edit' ? 'Edit Logo' : 'Add Logo';
    const removeLabel = 'Remove customisation';
    const actionIconMarkup = getCartActionIconMarkup(actionMode);
    const removeIconMarkup = getRemoveCustomisationIconMarkup();
    const label = actionMode === 'edit' ? 'Edit Logo' : 'Add Logo';
    const method = getLogoMethod(properties);
    const logoType = getLogoType(properties);
    const tooltip = hasCustomisationApplied ? buildCustomisationTooltip(properties) : '';
    const tooltipText = tooltip ? tooltip.replace(/\n+/g, ' • ') : '';
    const methodIconMarkup = hasCustomisationApplied && method
      ? `<div class="new-logo-method-icon new-logo-method-icon--${escapeHtml(method)}" aria-label="${escapeHtml(
          method
        )}">${getMethodIconMarkup(method)}</div>`
      : '';
    const iconGroupMarkup = methodIconMarkup
      ? `<div class="new-logo-method-icons"${
          tooltipText ? ` data-tooltip="${escapeHtml(tooltipText)}"` : ''
        } aria-label="${escapeHtml(tooltipText || method)}" tabindex="0">${methodIconMarkup}</div>`
      : '';
    const urlBase = resolveCustomiserUrlSync(group, '');

    const url = new URL(urlBase, window.location.origin);
    url.searchParams.set('line', String(line));
    if (cartItem.key) {
      url.searchParams.set('key', String(cartItem.key));
    }
    if (group) {
      url.searchParams.set('customisation_group', group);
    }

    let actionWrap = actionHost.querySelector('.new-logo-cart-action');
    if (!actionWrap) {
      actionWrap = document.createElement('div');
      actionWrap.className = 'new-logo-cart-action';
      actionHost.appendChild(actionWrap);
    }

    const actionMarkup = `
      <div class="new-logo-cart-action__inline">
        <span class="new-logo-link__left">
          <span class="new-logo-link new-logo-link--static" aria-label="${escapeHtml(label)}"><span class="new-logo-link__label">${escapeHtml(label)}</span></span>
          <a class="btn--link new-logo-link new-logo-link--icon-trigger" href="${escapeHtml(
            url.toString()
          )}" data-new-logo-link data-line="${line}" data-key="${escapeHtml(String(cartItem.key || ''))}" data-group="${escapeHtml(
            group
          )}" aria-label="${escapeHtml(triggerLabel)}"><span class="new-logo-link__icon" aria-hidden="true">${actionIconMarkup}</span></a>
          ${
            hasCustomisationApplied
              ? `<button type="button" class="btn--link new-logo-link new-logo-link--icon-trigger new-logo-link--remove-trigger" data-new-logo-remove data-line="${line}" data-key="${escapeHtml(
                  String(cartItem.key || '')
                )}" data-logo-token="${escapeHtml(String(properties._logo_customiser_token || ''))}" data-variant-id="${escapeHtml(
                  String(cartItem.variant_id ?? cartItem.id ?? '')
                )}" aria-label="${escapeHtml(removeLabel)}"><span class="new-logo-link__icon" aria-hidden="true">${removeIconMarkup}</span></button>`
              : ''
          }
        </span>
        ${iconGroupMarkup}
      </div>
    `;
    const actionSignature = `${actionMode}|${url.toString()}|${method}|${logoType}|${tooltipText}|${group}|${String(
      cartItem.key || ''
    )}`;

    if (actionWrap.dataset.actionSignature !== actionSignature) {
      actionWrap.innerHTML = actionMarkup;
      actionWrap.dataset.actionSignature = actionSignature;
    }

    itemEl.setAttribute('data-cart-item-key', String(cartItem.key || ''));
    itemEl.setAttribute('data-customisation-group', group);
  }

  async function syncCartLineActions(cartHint, forceSync = false) {
    if (!forceSync && !shouldSyncCartLineActions()) {
      emitCartActionsSynced('skipped-context');
      return;
    }
    if (STATE.cartActionsSyncInFlight) {
      const queuedCart = getCartPayload(cartHint);
      if (queuedCart) {
        STATE.pendingCartPayload = queuedCart;
        STATE.latestCartPayload = queuedCart;
      }
      emitCartActionsSynced('already-running');
      return;
    }

    const cartItems = Array.from(document.querySelectorAll('.cart-item'));
    if (!cartItems.length) {
      emitCartActionsSynced('no-cart-items');
      return;
    }

    STATE.cartActionsSyncInFlight = true;

    try {
      const payloadCart =
        getCartPayload(cartHint) || (!forceSync ? getCartPayload(STATE.latestCartPayload) : null);
      const cart = payloadCart || (await fetchCartJson());
      if (payloadCart) {
        STATE.latestCartPayload = payloadCart;
      }

      const safeItems = Array.isArray(cart?.items) ? cart.items : [];
      const elementMap = buildCartItemMapByElement(cartItems, safeItems);

      cartItems.forEach((itemEl) => {
        const matched = elementMap.get(itemEl) || null;
        const cartItem = matched?.item || null;
        if (!cartItem) return;

        const resolvedLine =
          (Number.isFinite(matched?.index) ? matched.index + 1 : null) || parseLineFromElement(itemEl) || 1;

        if (cartItem?.key) {
          itemEl.setAttribute('data-cart-item-key', String(cartItem.key));
        }

        syncNativeLineBindings(itemEl, cartItem, resolvedLine);
        upsertCartLineAction(itemEl, cartItem, resolvedLine);
      });
    } catch (error) {
      console.error(error);
    } finally {
      STATE.cartActionsSyncInFlight = false;
      emitCartActionsSynced('completed');
      const queuedCart = STATE.pendingCartPayload;
      if (queuedCart) {
        STATE.pendingCartPayload = null;
        syncCartLineActions(queuedCart, true).catch((error) => {
          console.error(error);
        });
      }
    }
  }

  function scheduleCartLineActionsSync(delay = 180, force = false, cartHint = null) {
    if (!force && !shouldSyncCartLineActions()) return;
    const payloadCart = getCartPayload(cartHint);
    if (payloadCart) {
      STATE.pendingCartPayload = payloadCart;
      STATE.latestCartPayload = payloadCart;
    } else if (force) {
      // For forced refresh cycles, avoid replaying stale cached payloads.
      STATE.pendingCartPayload = null;
    }

    if (STATE.cartActionsSyncTimer) {
      clearTimeout(STATE.cartActionsSyncTimer);
    }

    if (delay <= 0) {
      const queuedCart = STATE.pendingCartPayload;
      STATE.pendingCartPayload = null;
      syncCartLineActions(queuedCart, force).catch((error) => {
        console.error(error);
      });
      return;
    }

    STATE.cartActionsSyncTimer = setTimeout(() => {
      STATE.cartActionsSyncTimer = null;
      const queuedCart = STATE.pendingCartPayload;
      STATE.pendingCartPayload = null;
      syncCartLineActions(queuedCart, force).catch((error) => {
        console.error(error);
      });
    }, delay);
  }

  function setupCartActionHandlers() {
    document.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-new-logo-remove]');
      if (!removeButton) return;

      event.preventDefault();
      event.stopPropagation();

      const line = Number.parseInt(removeButton.getAttribute('data-line') || '', 10);
      const key = String(removeButton.getAttribute('data-key') || '').trim();
      const token = String(removeButton.getAttribute('data-logo-token') || '').trim();
      const variantId = String(removeButton.getAttribute('data-variant-id') || '').trim();
      const removeStateKey = key || (Number.isFinite(line) && line > 0 ? `line-${line}` : '') || token;
      if (!removeStateKey) return;
      if (STATE.removingCustomisationKeys.has(removeStateKey)) return;

      STATE.removingCustomisationKeys.add(removeStateKey);
      removeButton.classList.add('is-loading');
      removeButton.setAttribute('aria-disabled', 'true');
      if (removeButton instanceof HTMLButtonElement) {
        removeButton.disabled = true;
      }

      removeLineCustomisation({
        line,
        key,
        token,
        variantId,
        allowTokenFallback: false,
      })
        .then(async (changedCart) => {
          const cartPayload = await getFreshCartPayload(changedCart);
          if (cartPayload) {
            publishCartSectionsUpdate(cartPayload);
            dispatchCartUpdatedFromPayload(cartPayload, 'new-logo-remove');
          }

          const keepOpen = isCartDrawerOpen();
          if (!hasSectionsPayload(cartPayload)) {
            dispatchExplicitCartRefresh(keepOpen, 'remove-customisation');
          }
          scheduleCartLineActionsSync(cartPayload ? 0 : 160, true, cartPayload);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          STATE.removingCustomisationKeys.delete(removeStateKey);
          removeButton.classList.remove('is-loading');
          removeButton.removeAttribute('aria-disabled');
          if (removeButton instanceof HTMLButtonElement) {
            removeButton.disabled = false;
          }
        });
    });

    document.addEventListener('click', (event) => {
      const logoLink = event.target.closest('[data-new-logo-link]');
      if (!logoLink) return;

      event.preventDefault();
      closeCartDrawerIfOpen();

      const url = logoLink.getAttribute('href');
      if (!url) return;

      openCustomiserModalFromUrl(url).catch((error) => {
        console.error(error);
        window.location.href = url;
      });
    });

    const onLogoCustomiserSaved = async (event) => {
      closeCustomiserModal();

      if (document.body.classList.contains('cart-template')) {
        window.location.reload();
        return;
      }

      const cartPayload = await getFreshCartPayload(event?.detail?.cart || null);
      if (cartPayload) {
        publishCartSectionsUpdate(cartPayload);
        dispatchCartUpdatedFromPayload(cartPayload, 'logo-customiser');
      }
      if (hasSectionsPayload(cartPayload)) {
        // Sections are already fresh: just open drawer without forcing another refresh cycle.
        setTimeout(() => {
          openCartDrawer();
        }, 0);
      } else {
        dispatchExplicitCartRefresh(true, 'logo-customiser-save');
      }
      scheduleCartLineActionsSync(cartPayload ? 0 : 160, true, cartPayload);
    };
    window.addEventListener('logo-customiser:saved', onLogoCustomiserSaved);
    document.addEventListener('logo-customiser:saved', onLogoCustomiserSaved);

    document.addEventListener('cart:updated', (event) => {
      STATE.lastCartUpdatedAt = Date.now();
      const cartPayload = getCartPayload(event?.detail?.cart);
      const force = String(event?.detail?.source || '') === 'logo-customiser';
      scheduleCartLineActionsSync(cartPayload ? 0 : 120, force, cartPayload);
    });

    document.addEventListener('cart:refresh', (event) => {
      const shouldForce = Boolean(event?.detail?.open);
      scheduleCartLineActionsSync(360, shouldForce);
    });

    if (STATE.cartActionsObserver) {
      STATE.cartActionsObserver.disconnect();
    }
    STATE.cartActionsObserver = new MutationObserver((mutations) => {
      if (!shouldSyncCartLineActions()) return;

      const hasRelevantMutation = mutations.some((mutation) => {
        const targetNode = mutation.target;
        const element =
          targetNode && targetNode.nodeType === 1 ? targetNode : targetNode?.parentElement || null;
        if (!element || !(element instanceof Element)) return false;
        if (element.closest('.new-logo-cart-action')) return false;
        return Boolean(element.closest('cart-drawer, main-cart-items, .cart-template, .cart-item'));
      });

      if (!hasRelevantMutation) return;
      if (Date.now() - STATE.lastCartUpdatedAt < 420) return;
      scheduleCartLineActionsSync(260);
    });
    STATE.cartActionsObserver.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      if (STATE.cartDrawerOpenObserver) {
        STATE.cartDrawerOpenObserver.disconnect();
      }
      STATE.cartDrawerOpenObserver = new MutationObserver((mutations) => {
        const opened = mutations.some(
          (mutation) => mutation.type === 'attributes' && mutation.attributeName === 'open' && cartDrawer.hasAttribute('open')
        );
        if (opened) {
          scheduleCartLineActionsSync(120, true);
        }
      });
      STATE.cartDrawerOpenObserver.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['open'],
      });
    }
  }

  function init() {
    setupCartActionHandlers();
    scheduleCartLineActionsSync(60);
  }

  init();

  window.NewLogoOverlay = {
    init,
    scheduleCartLineActionsSync,
  };
})();
